import React, { useEffect } from "react";
import moment from "moment";
import "moment/locale/ja";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import FrontLayout from "../../../components/layout-new-theme/FrontLayout";
import SkeletonLoading from "../booking-detail/_components/SkeletonLoading";
import { BackIcon } from "../../../components/icons";
import { cn } from "../../../utils/classnames";
import { newGetBookings } from "../../../actions/auth/booking";

const STATUS = {
  PENDING_APPROVAL: "PENDING_APPROVAL",
  REJECTED: "REJECTED",
  APPROVED: "APPROVED",
  UPDATED: "UPDATED",
};

const STATUS_STYLES = {
  PENDING_APPROVAL: {
    labels: {
      en: "Change Requested",
      ja: "変更依頼中",
    },
    className: "tw:bg-[#20639B1A] tw:text-[#20639B]",
  },
  REJECTED: {
    labels: {
      en: "Change Rejected",
      ja: "変更手配不可",
    },
    className: "tw:bg-[#FFEAE7] tw:text-[#EA4335]",
  },
  APPROVED: {
    labels: {
      en: "Change Approved",
      ja: "変更承認済み",
    },
    className: "tw:bg-[#DBF4F1] tw:text-[#18B1A3]",
  },
  UPDATED: {
    labels: {
      en: "Updated",
      ja: "更新済み",
    },
    className: "tw:bg-[#F9EDD3] tw:text-[#A3780F]",
  },
};


const getStatusMeta = (status, responseBy) => {
  console.log("status", status, "responseBy", responseBy);
  return status === STATUS.APPROVED && !responseBy ? STATUS_STYLES.UPDATED : STATUS_STYLES[String(status || "").toUpperCase()];
};

const TABLE_HEADER_LABELS = {
  editedBy: {
    en: "Edited By",
    ja: "更新者",
  },
  on: {
    en: "On",
    ja: "更新日時",
  },
  modificationStatus: {
    en: "Modification Status",
    ja: "予約変更状況",
  },
  lastUpdatedByAdmin: {
    en: "Last Updated by Admin",
    ja: "最終更新日時（管理者）",
  },
};

const formatDateTime = (dateValue, lang) => {

  if (!dateValue) return "-";
  const locale = lang === "en" ? "en" : "ja";
  const formatStr = lang === "en" ? "MMM D, YYYY H:mm" : "YYYY年MM月DD日 H:mm";
  return moment(dateValue).local().locale(locale).format(formatStr);
};

const ViewDetailIcon = ({ size = 16, color = "#ffffff" }) => (
  <svg width={size} height={size} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path
      d="M10.0005 3.75C14.1668 3.75013 17.7248 6.34201 19.1665 10C17.7249 13.6582 14.167 16.2499 10.0005 16.25C5.83382 16.25 2.27516 13.6583 0.833496 10C2.27526 6.34186 5.83396 3.75 10.0005 3.75ZM10.0005 5.83398C7.70063 5.83398 5.83372 7.70019 5.8335 10C5.8335 12.3 7.70049 14.167 10.0005 14.167C12.3003 14.1668 14.1665 12.2999 14.1665 10C14.1663 7.7003 12.3002 5.83416 10.0005 5.83398ZM10.0005 7.5C11.3835 7.50017 12.5003 8.61697 12.5005 10C12.5005 11.3832 11.3837 12.4998 10.0005 12.5C8.61716 12.5 7.50049 11.3833 7.50049 10C7.50071 8.61686 8.61729 7.5 10.0005 7.5Z"
      fill={color}
    />
  </svg>
);

const BookingDetailModificationHistoryPage = ({ match }) => {
  const { i18n } = useTranslation();
  const currentLanguage = i18n.language;
  const dispatch = useDispatch();
  const { loading, authDetailBooking } = useSelector((state) => state.authBooking);
  const bookingId = match?.params?.id;

  const isJapanese = ["jp", "ja"].includes((currentLanguage || "").toLowerCase());
  // const baseFontClass = isJapanese ? "tw:font-libre-caslon-display" : "tw:font-hanken-grotesk";

  useEffect(() => {
    if (!bookingId) return;
    dispatch(newGetBookings({ id: bookingId }, "detail"));
  }, [dispatch, bookingId]);

  const requestRows = Array.isArray(authDetailBooking?.requestChangeBooking)
    ? authDetailBooking.requestChangeBooking
    : [];

  const sortedRows = [...requestRows].filter((a) => a.status !== "DRAFT").sort((a, b) => new Date(b?.date || b?.createdAt || 0) - new Date(a?.date || a?.createdAt || 0));

  const reservationHolderName = `${authDetailBooking?.customer?.pronounced || ""} ${authDetailBooking?.customer?.name || "-"}`.trim();
  const bookingIdLabel = isJapanese ? "予約番号" : "Booking ID";
  const headerSubtitle = isJapanese
    ? `${reservationHolderName} • ${bookingIdLabel} #${authDetailBooking?.key || "-"}`
    : `For ${reservationHolderName} • ${bookingIdLabel} #${authDetailBooking?.key || "-"}`;
  const tableHeaderLabels = {
    editedBy: isJapanese ? TABLE_HEADER_LABELS.editedBy.ja : TABLE_HEADER_LABELS.editedBy.en,
    on: isJapanese ? TABLE_HEADER_LABELS.on.ja : TABLE_HEADER_LABELS.on.en,
    modificationStatus: isJapanese ? TABLE_HEADER_LABELS.modificationStatus.ja : TABLE_HEADER_LABELS.modificationStatus.en,
    lastUpdatedByAdmin: isJapanese ? TABLE_HEADER_LABELS.lastUpdatedByAdmin.ja : TABLE_HEADER_LABELS.lastUpdatedByAdmin.en,
  };

  const isLoading = loading || !authDetailBooking || Object.keys(authDetailBooking).length === 0;

  if (isLoading) {
    return (
      <FrontLayout>
        <SkeletonLoading />
      </FrontLayout>
    );
  }

  return (
    <FrontLayout>
      <div className={cn("tw:bg-gradient-to-b tw:from-[#f6f6f6] tw:to-white tw:pb-8 tw:!pt-10 tw:font-hanken-grotesk")}>
        <div className="tw:mx-auto tw:max-w-[1300px] tw:px-0 tw:lg:px-6">
          <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-6 tw:pb-[28px] tw:lg:pb-[36px] tw:px-5 tw:lg:px-0">
            <Link
              to={`/booking-detail/${bookingId}`}
              className="tw:inline-flex tw:items-center tw:gap-[10px] tw:rounded-[5px] tw:border tw:border-[#e8e8e8] tw:bg-[#e8e8e8] tw:px-3 tw:py-2 tw:text-[#282828] tw:!no-underline"
            >
              <BackIcon color="#8D8D8D" width={7} height={10} />
              <span className={cn("tw:font-hanken-grotesk tw:text-[15px] tw:leading-[22px] tw:text-[#282828]")}>{isJapanese ? "戻る" : "Back"}</span>
            </Link>
            <div className="tw:flex tw:flex-col tw:gap-[6px]">
              <div className={cn("tw:font-hanken-grotesk tw:text-[15px] tw:font-[500] tw:leading-[22px] tw:text-[#8D8D8D]")}>
                {headerSubtitle}
              </div>
              <div className={cn("tw:font-hanken-grotesk tw:text-[42px] tw:font-[600] tw:leading-[52px] tw:text-[#282828] tw:[zoom:0.5] tw:[transform-origin:left_top]")}>
                {`${isJapanese ? "変更履歴" : "Modification History"} (${sortedRows.filter(item => item.requestType === 'reschedule').length})`}
              </div>
            </div>
          </div>

          <div className="">
            <div className="tw:overflow-x-auto tw:overflow-y-hidden tw:rounded-[5px] tw:border tw:border-[#E8E8E8] tw:bg-white">
              <table className="tw:w-full tw:min-w-[860px] tw:border-collapse">
                <thead>
                  <tr className="tw:bg-[#282828]">
                    <th className={cn("tw:font-hanken-grotesk tw:whitespace-nowrap tw:px-4 tw:py-[19px] tw:text-left tw:text-[15px] tw:font-[700] tw:leading-[22px] tw:text-white")}>
                      {tableHeaderLabels.editedBy}
                    </th>
                    <th className={cn("tw:font-hanken-grotesk tw:whitespace-nowrap tw:px-4 tw:py-[19px] tw:text-left tw:text-[15px] tw:font-[700] tw:leading-[22px] tw:text-white")}>
                      {tableHeaderLabels.on}
                    </th>
                    <th className="tw:w-10 tw:px-2 tw:py-[19px] tw:text-center tw:text-white">↕</th>
                    <th className={cn("tw:font-hanken-grotesk tw:whitespace-nowrap tw:px-4 tw:py-[19px] tw:text-left tw:text-[15px] tw:font-[700] tw:leading-[22px] tw:text-white")}>
                      {tableHeaderLabels.modificationStatus}
                    </th>
                    <th className="tw:w-10 tw:px-2 tw:py-[19px] tw:text-center tw:text-white">↕</th>
                    <th className={cn("tw:font-hanken-grotesk tw:whitespace-nowrap tw:px-4 tw:py-[19px] tw:text-left tw:text-[15px] tw:font-[700] tw:leading-[22px] tw:text-white")}>
                      {tableHeaderLabels.lastUpdatedByAdmin}
                    </th>
                    <th className="tw:sticky tw:right-0 tw:z-20 tw:w-[56px] tw:border-l tw:border-[#3A3A3A] tw:bg-[#282828] tw:px-2 tw:py-[19px] tw:text-center tw:text-white" />
                  </tr>
                </thead>
                <tbody>
                  {sortedRows.length ? (
                    sortedRows.filter(item => item.requestType === 'reschedule')
                      .sort((a, b) => new Date(b?.date || b?.createdAt || 0) - new Date(a?.date || a?.createdAt || 0))
                      .map((row) => {
                        const statusMeta = getStatusMeta(row?.status, row?.responseBy);
                        const statusLabel = isJapanese ? statusMeta?.labels?.ja || statusMeta?.labels?.en : statusMeta?.labels?.en;
                        const isPending = String(row?.status || "").toUpperCase() === "PENDING_APPROVAL";

                        return (
                          <tr key={row?._id} className="tw:!border-b tw:!border-[#E8E8E8]">
                            <td className={cn("tw:font-hanken-grotesk tw:whitespace-nowrap tw:px-4 tw:!py-[18.5px] tw:text-[15px] tw:font-[400] tw:leading-[22px] tw:text-[#282828]")}>
                              {reservationHolderName || "-"}
                            </td>
                            <td className={cn("tw:font-hanken-grotesk tw:whitespace-nowrap tw:px-4 tw:!py-[18.5px] tw:text-[15px] tw:font-[400] tw:leading-[22px] tw:text-[#282828]")}>
                              {formatDateTime(row?.date || row?.createdAt, currentLanguage)}
                            </td>
                            <td className="tw:px-2 tw:!py-[18.5px]" />
                            <td className="tw:whitespace-nowrap tw:px-4 tw:!py-[18.5px]">
                              <div className={cn("tw:font-hanken-grotesk tw:inline-flex tw:min-w-[150px] tw:items-center tw:justify-center tw:rounded-[5px] tw:px-4 tw:py-2 tw:text-[15px] tw:leading-[22px]", statusMeta.className)}>
                                {statusLabel}
                              </div>
                            </td>
                            <td className="tw:px-2 tw:!py-[18.5px]" />
                            <td className={cn("tw:font-hanken-grotesk tw:whitespace-nowrap tw:px-4 tw:!py-[18.5px] tw:text-[15px] tw:font-[400] tw:leading-[22px] tw:text-[#282828]")}>
                              {isPending ? "-" : formatDateTime(row?.respondedAt, currentLanguage)}
                            </td>
                            <td className="tw:sticky tw:right-0 tw:z-10 tw:!border-l tw:!border-[#E8E8E8] tw:bg-white tw:px-2 tw:!py-[18.5px] tw:text-center">
                              <Link
                                to={`/booking-detail/${bookingId}/modification-history/${row?._id}`}
                                state={{ historyItem: row }}
                                className="tw:inline-flex tw:!no-underline"
                              >
                                <div className="tw:inline-flex tw:h-8 tw:w-8 tw:items-center tw:justify-center tw:rounded-[2px] tw:bg-[#DBBE78]">
                                  <ViewDetailIcon size={16} color="#ffffff" />
                                </div>
                              </Link>
                            </td>
                          </tr>
                        );
                      })
                  ) : (
                    <tr>
                      <td colSpan={7} className={cn("tw:font-hanken-grotesk tw:px-4 tw:!py-[18.5px] tw:text-center tw:text-[15px] tw:leading-[22px] tw:text-[#282828]")}>
                        No modification history found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </FrontLayout>
  );
};

export default BookingDetailModificationHistoryPage;
